import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { apiFetch, type SubscriptionSummary } from '@/lib/api';
import { logger } from '@/lib/logger';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  oauthProvider: string;
}

type AuthContextValue = {
  user: AuthUser | null;
  subscription: SubscriptionSummary | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  setSubscription: (subscription: SubscriptionSummary | null) => void;
  setUser: (user: AuthUser | null) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiFetch('/me');
      if (!response.ok) {
        // If /api/me fails, trigger logout
        setUser(null);
        setSubscription(null);
        setError(response.status === 401 ? null : `Auth fetch failed with status ${response.status}`);
        // Redirect to homepage on auth failure
        if (typeof window !== 'undefined' && window.location.pathname !== '/') {
          window.location.href = '/';
        }
        return;
      }

      const payload = await response.json().catch(() => ({}));
      setUser(payload?.user ?? null);
      setSubscription(payload?.subscription ?? null);
      setError(null);
    } catch (err) {
      logger.error('Auth fetch failed', err);
      setUser(null);
      setSubscription(null);
      setError('Unable to reach authentication service');
      // Redirect to homepage on auth failure
      if (typeof window !== 'undefined' && window.location.pathname !== '/') {
        window.location.href = '/';
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      subscription,
      loading,
      error,
      refresh: async () => {
        await refresh();
      },
      setSubscription,
      setUser,
    }),
    [user, subscription, loading, error, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
